import { VueConstructor } from 'vue';
import { VueAuth, Auth0Options, RedirectCallback } from './VueAuth';

type Auth0PluginOptions = {
  onRedirectCallback: RedirectCallback;
  redirectUri: string;
  domain: string;
  clientId: string;
  audience?: string;
  [key: string]: string | RedirectCallback | undefined;
};

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = (appState: any) =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance: VueAuth;

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new VueAuth();

  const AUTH0_DOMAIN = (process.env.VUE_APP_AUTH0_DOMAIN || '').replace(
    /\/+$/,
    ''
  );
  const AUTH0_CLIENT_ID = process.env.VUE_APP_AUTH0_CLIENT_ID;
  const AUTH0_AUDIENCE = process.env.VUE_APP_AUTH0_AUDIENCE;
  if (!AUTH0_DOMAIN || !AUTH0_CLIENT_ID || !AUTH0_AUDIENCE) {
    throw 'AUTH0_DOMAIN or AUTH0_CLIENT_ID or AUTH0_AUDIENCE not present';
  }
  instance.init(onRedirectCallback, redirectUri, {
    ...(options as Auth0Options),
    domain: AUTH0_DOMAIN,
    clientId: AUTH0_CLIENT_ID,
    useRefreshTokens: 'true',
    audience: AUTH0_AUDIENCE,
    advancedOptions: {
      defaultScope: 'openid name email email_verified',
    },
    cacheLocation: 'localstorage',
  });
  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue: VueConstructor, options: Auth0PluginOptions) {
    Vue.prototype.$auth = useAuth0(options);
  },
};
